import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Calendar as CalendarIcon, MapPin, Package, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Calendar = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDrops();
  }, []);

  const fetchDrops = async () => {
    try {
      const response = await axios.get(`${API}/drops`);
      // Filter to only show Casablanca and Emita drops
      const filteredDrops = response.data.filter(drop => 
        drop.id === 'casablanca' || drop.id === 'emita'
      );
      setDrops(filteredDrops);
    } catch (error) {
      console.error('Failed to fetch drops', error);
    } finally {
      setLoading(false);
    }
  };

  // Format date range
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options = { day: 'numeric', month: 'short' };
    const yearOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    
    return `${start.toLocaleDateString('en-GB', options)} to ${end.toLocaleDateString('en-GB', yearOptions)}`;
  };

  // Get route for each drop
  const getJoinRoute = (dropId) => {
    if (dropId === 'casablanca') return '/join/casablanca';
    if (dropId === 'emita') return '/join/emita';
    return `/join/${dropId}`;
  };

  const DropCard = ({ drop }) => {
    return (
      <Card 
        className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200" 
        data-testid={`drop-card-${drop.id}`}
      >
        <CardContent className="p-6">
          {/* Header row */}
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-playfair font-semibold text-gray-900">
              {drop.brand}
            </h2>
            {drop.status && (
              <Badge 
                className={`${drop.status === 'open' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-700 border-gray-200'} rounded-full px-3 py-1 text-xs font-medium border`}
                data-testid={`drop-status-${drop.id}`}
              >
                {drop.status.charAt(0).toUpperCase() + drop.status.slice(1)}
              </Badge>
            )}
          </div>

          {/* Details list */}
          <div className="space-y-3 mb-6">
            {/* Date range */}
            <div className="flex items-center gap-3 text-gray-600">
              <CalendarIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm">{formatDateRange(drop.start_date, drop.end_date)}</span>
            </div>

            {/* Location */}
            <div className="flex items-center gap-3 text-gray-600">
              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm">{drop.location || 'Singapore'}</span>
            </div>

            {/* Collection */}
            <div className="flex items-center gap-3 text-gray-600">
              <Package className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm">Collection: {drop.collection_info || 'To be confirmed'}</span>
            </div>

            {/* Participants */}
            <div className="flex items-center gap-3 text-gray-600">
              <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm">
                Participants: {drop.participant_count > 0 ? drop.participant_count : 'TBD'}
              </span>
            </div>
          </div>

          {/* CTA Button - Always "Join Drop" */}
          <Link to={getJoinRoute(drop.id)}>
            <Button
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg py-3 font-medium transition-colors"
              data-testid={`join-drop-button-${drop.id}`}
            >
              Join Drop
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5EFE6] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5EFE6]" data-testid="calendar-page">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-2xl font-playfair font-bold text-primary tracking-tight">
            Sharedequal
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/calendar" className="text-sm font-medium text-primary">
              Calendar
            </Link>
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-sm">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-sm">
                  Login
                </Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Page Header */}
        <div className="mb-10">
          <h1 
            className="text-4xl font-playfair font-bold text-gray-900 tracking-tight" 
            data-testid="calendar-heading"
          >
            Drop Calendar
          </h1>
          <p className="text-gray-600 mt-2 text-base">
            Active and upcoming group orders
          </p>
        </div>

        {/* Drop Cards Grid - 2 columns on desktop */}
        {drops.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {drops.map(drop => (
              <DropCard key={drop.id} drop={drop} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No active drops at the moment</p>
            <p className="text-gray-400 mt-2">Check back soon</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Calendar;
